import { SelectProps } from '@round/ui-kit';
import { GenericDropdownOption } from '../../utils/utils.types';
import { useCallback, useMemo, useState } from 'react';
import { ValueType } from 'react-select';
import { ApexInfluencer, getApexInfluencer, getApexInfluencers } from '@round/api';
import { showNotification } from '../../utils/showNotification';
import useAbortableEffect from '../../hooks/useAbortableEffect';
import debounce from 'lodash/debounce';

const OPTIONS_PAGE_SIZE = 25;
type UseCreatorSelectReturn = Pick<
    SelectProps<GenericDropdownOption<number>, false>,
    | 'isLoading'
    | 'noOptionsMessage'
    | 'value'
    | 'onChange'
    | 'options'
    | 'inputValue'
    | 'onInputChange'
    | 'onMenuScrollToBottom'
    | 'onMenuOpen'
    | 'onMenuClose'
>;

export default function useCreatorSelect(initialCreatorId?: number): UseCreatorSelectReturn {
    const [isValueInitialized, setIsValueInitialized] = useState(false);
    const [value, setValue] = useState<ValueType<GenericDropdownOption<number>, false>>(null);

    const initValue = useCallback(async (initialCreatorId: number, requestInit?: RequestInit) => {
        try {
            const response = await getApexInfluencer(initialCreatorId, requestInit);
            if (response.status === 404) {
                return;
            }

            setValue({ value: response.data.id, label: response.data.name ?? '-' });
            setIsValueInitialized(true);
        } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') {
                return;
            }

            showNotification('Could not initialize creator select', 'error');
            setIsValueInitialized(true);
        }
    }, []);

    useAbortableEffect(
        (signal) => {
            if (!isValueInitialized && typeof initialCreatorId === 'number') {
                initValue(initialCreatorId, { signal });
            }
        },
        [initValue, initialCreatorId, isValueInitialized]
    );

    const [page, setPage] = useState(1);
    const [creators, setCreators] = useState<ApexInfluencer[]>([]);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [areOptionsLoading, setAreOptionsLoading] = useState(false);
    const [areOptionsInitialized, setAreOptionsInitialized] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [isSearchLoading, setIsSearchLoading] = useState(false);
    const [search, setSearch] = useState('');

    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const fetchOptions = useCallback(
        async (params: Parameters<typeof getApexInfluencers>[0], requestInit?: RequestInit) => {
            try {
                setAreOptionsLoading(true);
                const response = await getApexInfluencers(params, requestInit);
                if (response.status === 404) {
                    return;
                }

                setCreators((creators) => creators.concat(response.data.results));
                setHasNextPage(Boolean(response.data.next));
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                setError('Could not fetch creators');
            } finally {
                setAreOptionsLoading(false);
                setIsSearchLoading(false);
                setAreOptionsInitialized(true);
            }
        },
        []
    );

    const debouncedFetchOptions = useMemo(() => debounce(fetchOptions, 700), [fetchOptions]);

    const resetOptions = () => {
        setCreators([]);
        setHasNextPage(false);
        setAreOptionsLoading(false);
        setError(null);
        setAreOptionsInitialized(false);
    };

    useAbortableEffect(
        (signal) => {
            if (isMenuOpen && !areOptionsInitialized) {
                debouncedFetchOptions({ page, page_size: OPTIONS_PAGE_SIZE, search }, { signal });
            }
        },
        [areOptionsInitialized, debouncedFetchOptions, isMenuOpen, page, search]
    );

    return {
        options: useMemo(() => creators.map((c) => ({ value: c.id, label: c.name ?? '-' })), [creators]),
        value,
        onChange: setValue,
        inputValue: search,
        onInputChange: (value: string) => {
            if (search === value) {
                return;
            }

            resetOptions();
            setSearch(value);
            if (value) {
                setIsSearchLoading(true);
            }
        },
        isLoading: areOptionsLoading || isSearchLoading,
        noOptionsMessage: () => error ?? 'No options found',
        onMenuOpen: () => setIsMenuOpen(true),
        onMenuClose: () => setIsMenuOpen(false),
        onMenuScrollToBottom: () => {
            if (!hasNextPage || error) {
                return;
            }

            setPage((page) => page + 1);
        },
    };
}
